/**
 * ClientOnly Component
 *
 * Renders children only on the client-side after hydration completes.
 * This prevents hydration mismatches when using components that rely on
 * browser APIs or client-only features like React Context.
 *
 * Uses render prop pattern to defer child evaluation until after hydration.
 * This is critical because passing children as ReactNode evaluates them
 * during the render phase even when not displayed, which can cause
 * hydration mismatches if those children use browser APIs.
 *
 * Pattern:
 * - Server: Renders fallback (or null)
 * - Client (initial): Renders same fallback (or null) - NO MISMATCH
 * - Client (after hydration): useEffect sets mounted=true and children() is called
 *
 * Usage:
 * <ClientOnly fallback={<Loading />}>
 *   {() => <ComponentNeedingBrowser />}
 * </ClientOnly>
 */

import { useState, useEffect, ReactNode } from 'react';

interface ClientOnlyProps {
  /**
   * Render function that returns client-only content.
   * Using a function defers evaluation until after hydration.
   */
  children: () => ReactNode;
  /**
   * Content to show during SSR and initial hydration.
   */
  fallback?: ReactNode;
}

export function ClientOnly({ children, fallback = null }: ClientOnlyProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Both server and client render fallback initially (no hydration mismatch)
  // After hydration, useEffect sets mounted=true and children() is called
  return mounted ? <>{children()}</> : <>{fallback}</>;
}
